/**
 * The Shipyard (GDD §6.1): IPM's ships, three ways to get one. Each hull is
 * owned once, kept forever, and multiplies the fleet like a Room does.
 *
 *  - earned: unlocked by a milestone, free.
 *  - sc:     bought with ★ SC (IPM sells ships for Dark Matter-ish cash; ours cost SC).
 *  - store:  real money, as IPM's Daughtership/Eldership/Exodus. Prices are
 *            placeholders — there is no store in the prototype.
 *
 * Multipliers sit below IPM's (its Eldership is ×2 mining) because Rooms and
 * the Matrix already carry the long-term speed, and paid hulls must not make
 * the free game feel slow.
 */

export type ShipEffect = 'mine' | 'ship' | 'cargo' | 'smelt' | 'craft' | 'sell' | 'foldTe';

export const SHIP_EFFECT_LABEL: Readonly<Record<ShipEffect, string>> = {
  mine: 'Mining', ship: 'Ship speed', cargo: 'Cargo', smelt: 'Smelting', craft: 'Crafting', sell: 'Sell price', foldTe: 'TE at Fold',
};

export type ShipGoal =
  | { readonly kind: 'depth'; readonly planets: number }
  | { readonly kind: 'folds'; readonly folds: number }
  | { readonly kind: 'challenges'; readonly challenges: number };

export type ShipSource =
  | { readonly kind: 'earned'; readonly goal: ShipGoal }
  | { readonly kind: 'sc'; readonly cost: number }
  | { readonly kind: 'store'; readonly price: string; readonly sc: number };

export interface ShipDef {
  readonly id: string;
  readonly name: string;
  /** The IPM ship it echoes, if any. */
  readonly ipm?: string;
  readonly glyph: string;
  readonly color: string;
  /** Class shown on its card, like IPM's ship types. */
  readonly role: string;
  /** One-line tagline. */
  readonly text: string;
  /** A short story for the ship sheet. */
  readonly lore: string;
  readonly source: ShipSource;
  readonly mults: Readonly<Partial<Record<ShipEffect, number>>>;
}

export const SHIPS: readonly ShipDef[] = [
  // Earned
  { id: 'pathfinder', name: 'Pathfinder', glyph: '➶', color: '#7fe3ff', role: 'Scout', text: 'Charted the first worlds.',
    lore: "The Foundry's first surveyor. Light, fast and always a jump ahead of the haulers, it charted the routes every later ship still flies.",
    source: { kind: 'earned', goal: { kind: 'depth', planets: 10 } }, mults: { ship: 1.15, cargo: 1.15 } },
  { id: 'deepcutter', name: 'Deepcutter', glyph: '⛏', color: '#d9b13c', role: 'Driller', text: 'A drill ship for the far rings.',
    lore: "A cutting head the size of a moon's crater, bolted to an engine block. It does not ask a planet for its ore.",
    source: { kind: 'earned', goal: { kind: 'depth', planets: 20 } }, mults: { mine: 1.25 } },
  { id: 'ironwake', name: 'Ironwake', glyph: '⚒', color: '#ff8a3d', role: 'Forge hull', text: 'Twenty-five galaxies of forge time.',
    lore: "Armoured against its own furnaces. Its vents have not gone cold in twenty-five galaxies.",
    source: { kind: 'earned', goal: { kind: 'folds', folds: 25 } }, mults: { smelt: 1.2, craft: 1.2 } },
  { id: 'horizon_seeker', name: 'Horizon Seeker', glyph: '◎', color: '#b58cff', role: 'Explorer', text: 'Reached the last known world.',
    lore: "Built around a warp ring that hums at the edge of the known map. It came back from the last world with the charts.",
    source: { kind: 'earned', goal: { kind: 'depth', planets: 28 } }, mults: { foldTe: 1.25 } },
  { id: 'gauntlet', name: 'Gauntlet', glyph: '✦', color: '#ff3d7f', role: 'Raider', text: 'Ten Challenges run to the end.',
    lore: "Twin prongs and no patience. Challengers fly it to prove a galaxy can be run in a day.",
    source: { kind: 'earned', goal: { kind: 'challenges', challenges: 10 } }, mults: { mine: 1.1, sell: 1.1 } },
  // SC
  { id: 'ore_barge', name: 'Ore Barge', glyph: '⬢', color: '#8fd16a', role: 'Barge', text: 'Slow, wide, always full.',
    lore: "An open hopper with a cab on the front. Slow, wide, and never empty.",
    source: { kind: 'sc', cost: 150 }, mults: { mine: 1.15 } },
  { id: 'freight_hauler', name: 'Freight Hauler', glyph: '▤', color: '#4f9dff', role: 'Hauler', text: 'Bigger holds on every route.',
    lore: "A spine of stacked containers. Every route it flies carries twice the load.",
    source: { kind: 'sc', cost: 300 }, mults: { cargo: 1.2, ship: 1.1 } },
  { id: 'forge_tender', name: 'Forge Tender', glyph: '♨', color: '#ff6a4a', role: 'Tender', text: 'A floating smeltery.',
    lore: "A floating smeltery. Its dome glows wherever the fleet is refining.",
    source: { kind: 'sc', cost: 600 }, mults: { smelt: 1.2, craft: 1.2 } },
  { id: 'trade_clipper', name: 'Trade Clipper', glyph: '◇', color: '#3be8b0', role: 'Clipper', text: 'First to every market.',
    lore: "Solar sails and a light hull. It reaches every market first and sells before the price turns.",
    source: { kind: 'sc', cost: 1000 }, mults: { sell: 1.2 } },
  // Store (£)
  { id: 'vanguard', name: 'Vanguard', ipm: 'Daughtership', glyph: '▲', color: '#dfe8ff', role: 'Flagship', text: 'The supporter hull.',
    lore: "The supporter's hull: layered wings in silver and gold trim. It leads the fleet out of every Fold.",
    source: { kind: 'store', price: '£4.99', sc: 100 }, mults: { mine: 1.3, ship: 1.15, cargo: 1.15 } },
  { id: 'tithe_barque', name: 'Tithe Barque', ipm: 'Exodus', glyph: '⟁', color: '#ffcf6b', role: 'Reliquary', text: 'Every Fold pays more.',
    lore: "Crescent sails round a sealed reliquary. Whatever a galaxy is worth, the Barque collects a little more.",
    source: { kind: 'store', price: '£9.99', sc: 0 }, mults: { foldTe: 1.5 } },
  { id: 'sovereign', name: 'Sovereign', ipm: 'Eldership', glyph: '♛', color: '#9b7bff', role: 'Dreadnought', text: 'A flagship for the whole fleet.',
    lore: "Decks, batteries and a bridge tower: a city with engines. The whole fleet works harder in its wake.",
    source: { kind: 'store', price: '£14.99', sc: 400 }, mults: { mine: 1.5, ship: 1.25, cargo: 1.25, smelt: 1.25, craft: 1.25 } },
];

export function shipDef(id: string): ShipDef {
  const def = SHIPS.find((s) => s.id === id);
  if (!def) throw new Error(`unknown ship ${id}`);
  return def;
}
